import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, Rect } from 'react-native-svg';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationsApi, organizationalUnitsApi, unitGoalsApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

import { API_BASE_URL } from '../../constants/config';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import { useNotification } from '../../contexts/NotificationContext';
import { Organization, OrganizationalUnit } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, Breadcrumb } from '../../components';
import { DashboardSkeleton } from '../../components/Skeleton';
import OrganizationalChart from '../../components/OrgChart';
import { useAuth } from '../../contexts/AuthContext';
import { OrganizationBanner } from './components';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { subscribe as subscribeInvalidation } from '../../utils/dataInvalidation';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { IconButton } from '@design/components/IconButton';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Extended unit type with dashboard fields
interface DepartmentUnit extends OrganizationalUnit {
  description?: string;
  children_count?: number;
  goals_count?: number;
  kpis_count?: number;
  processes_count?: number;
  employees_count?: number;
  achievement_percentage?: number;
}

// Icons as SVG components
const EditIcon = () => (
  <Svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <Path
      d="M10.625 1.875L13.125 4.375M1.25 13.75L1.875 10.9375L10.9375 1.875L13.125 4.0625L4.0625 13.125L1.25 13.75Z"
      stroke={tokens.colors.raw.slate["500"]}
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const DepartmentIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Rect x="10" y="3.33" width="20" height="33.33" rx="2" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" />
    <Rect x="3.33" y="20" width="6.67" height="16.67" rx="1" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" />
    <Rect x="30" y="15" width="6.67" height="21.67" rx="1" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" />
    <Path d="M16.67 10H23.33M16.67 16.67H23.33M16.67 23.33H23.33M16.67 30H23.33" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

const SectionsIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Rect x="10" y="3.33" width="20" height="33.33" rx="2" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" />
    <Rect x="3.33" y="20" width="6.67" height="16.67" rx="1" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" />
    <Rect x="30" y="15" width="6.67" height="21.67" rx="1" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" />
    <Path d="M16.67 10H23.33M16.67 16.67H23.33M16.67 23.33H23.33M16.67 30H23.33" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

const EmployeesIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path
      d="M28.33 35V31.67C28.33 29.9 27.63 28.20 26.38 26.95C25.13 25.70 23.43 25 21.67 25H8.33C6.57 25 4.87 25.70 3.62 26.95C2.37 28.20 1.67 29.9 1.67 31.67V35"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M15 18.33C18.68 18.33 21.67 15.35 21.67 11.67C21.67 7.98 18.68 5 15 5C11.32 5 8.33 7.98 8.33 11.67C8.33 15.35 11.32 18.33 15 18.33Z"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M38.33 35V31.67C38.33 30.19 37.84 28.76 36.94 27.59C36.03 26.42 34.76 25.59 33.33 25.22"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M26.67 5.22C28.11 5.58 29.38 6.42 30.29 7.59C31.20 8.76 31.68 10.19 31.68 11.67C31.68 13.16 31.20 14.58 30.29 15.75C29.38 16.93 28.11 17.77 26.67 18.13"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const GoalsIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path
      d="M33.33 3.33V10H26.67"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M28.33 36.67C32.94 33.33 36.67 28.33 36.67 21.67C36.67 12.46 29.21 5 20 5C10.79 5 3.33 12.46 3.33 21.67C3.33 30.88 10.79 38.33 20 38.33"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M20 30C24.60 30 28.33 26.27 28.33 21.67C28.33 17.06 24.60 13.33 20 13.33C15.40 13.33 11.67 17.06 11.67 21.67C11.67 26.27 15.40 30 20 30Z"
      stroke={tokens.colors.semantic.general.foreground}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Circle cx="20" cy="21.67" r="3.33" fill={tokens.colors.semantic.general.foreground} />
  </Svg>
);

const PlusIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M8 3.33333V12.6667M3.33333 8H12.6667"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const SquarePlusIcon = () => (
  <Svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <Path
      d="M7 4.375V9.625M4.375 7H9.625"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M1.75 3.5C1.75 2.53 2.53 1.75 3.5 1.75H10.5C11.47 1.75 12.25 2.53 12.25 3.5V10.5C12.25 11.47 11.47 12.25 10.5 12.25H3.5C2.53 12.25 1.75 11.47 1.75 10.5V3.5Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ArrowLeftIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M10 12L6 8L10 4"
      stroke="#737373"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const TrashIcon = ({ color = tokens.colors.raw.red["500"] }: { color?: string }) => (
  <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 6V20C19 21.1 18.1 22 17 22H7C5.9 22 5 21.1 5 20V6M8 6V4C8 2.9 8.9 2 10 2H14C15.1 2 16 2.9 16 4V6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Circular Progress Component - 80x80 with dynamic color based on achievement
const CircularProgress = ({ percentage, size = 80, strokeWidth = 6 }: { percentage: number; size?: number; strokeWidth?: number }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  let progressColor: string = tokens.colors.raw.red["500"]; // Red - Needs Improvement (< 80%)
  if (percentage >= 100) progressColor = tokens.colors.raw.green["500"]; // Green - Excellent
  else if (percentage >= 80) progressColor = tokens.colors.raw.orange["500"]; // Orange - Good

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E5E5E5"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <Text style={styles.progressText}>%{percentage}</Text>
    </View>
  );
};

// Stat Row Component for department cards - Figma style (label pill on right, value on left)
const StatRow = ({ label, value }: { label: string; value: number | string }) => (
  <View style={styles.statRowContainer}>
    <Text style={styles.statRowValue}>{value}</Text>
    <View style={styles.statLabelPill}>
      <Text style={styles.statLabelText}>{label}:</Text>
    </View>
  </View>
);

export default function MyOrganizationScreen() {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<any>();
  const { navigateToHome, canAdd, canEdit, canDelete } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardWidth } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg, translateUnit } = useAutoTranslate();
  const scrollViewRef = useRef<ScrollView>(null);
  const unitsSectionY = useRef<number>(0);

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const orgId = user?.profile?.organization;

  // Deletion state - enhanced with DeletionImpactModal
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState<DepartmentUnit | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Org structure file state
  const [orgStructureFile, setOrgStructureFile] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [orgStructureAspectRatio, setOrgStructureAspectRatio] = useState<number>(16 / 9);
  const [orgStructureZoom, setOrgStructureZoom] = useState(0.8);

  const isOrgEmployee = user?.profile?.user_type === 'org_employee';

  const handleEditDept = (dept: DepartmentUnit) => {
    navigation.navigate('AddOrganizationalUnit', {
      unitId: dept.id,
      parentId: dept.parent ?? undefined,
      parentName: dept.parent_name ?? undefined,
    });
  };

  const handleDeleteDept = (dept: DepartmentUnit) => {
    setDeptToDelete(dept);
    setShowDeletionImpact(true);
  };

  const confirmDeleteDept = async () => {
    if (!deptToDelete) return;
    setIsDeleting(true);
    try {
      const response = await organizationalUnitsApi.delete(deptToDelete.id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        fetchData();
      } else {
        showSuccess(t('myOrganization.deleteSuccess'));
        // Invalidate + refetch — axios interceptor will also fire but this
        // makes the delete feel instant.
        fetchData();
      }
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        fetchData();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('myOrganization.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
      setDeptToDelete(null);
    }
  };

  const getFullMediaUrl = (path: string | null) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    // API_BASE_URL is like http://localhost:8002/api/v1 — extract origin
    const origin = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
    return `${origin}${path}`;
  };

  const handleFileSelected = async (file: File) => {
    if (!orgId) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const response = await organizationsApi.uploadOrgStructure(orgId, formData);
      setOrgStructureFile(getFullMediaUrl(response.org_structure_file));
      showSuccess(t('orgChart.uploadSuccess'));
    } catch (err) {
      console.error('Error uploading org structure:', err);
      showError(t('orgChart.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteOrgStructureFile = async () => {
    if (!orgId) return;
    try {
      await organizationsApi.deleteOrgStructure(orgId);
      setOrgStructureFile(null);
      showSuccess(t('orgChart.deleteSuccess'));
    } catch (err) {
      console.error('Error deleting org structure file:', err);
      showError(t('orgChart.deleteFailed'));
    }
  };


  // Measure uploaded image aspect ratio for proper scaling
  useEffect(() => {
    if (orgStructureFile) {
      Image.getSize(orgStructureFile, (width, height) => {
        if (width && height) setOrgStructureAspectRatio(width / height);
      }, () => {});
    }
  }, [orgStructureFile]);

  // Responsive card width for department cards
  const departmentCardWidth = cardWidth.third;

  // Organization detail query — React Query caches the result in the global
  // QueryClient so it survives component unmount/remount across navigation.
  // A fresh mount renders cached data instantly (no spinner) and refetches
  // in the background only if the data is stale.
  //
  // The API may return either the plain org dict or a dashboard-wrapped
  // shape `{ organization: {...}, statistics: {...} }` — unwrap the latter
  // so downstream code can keep reading `organization.name` etc.
  const {
    data: organization,
    isLoading: organizationLoading,
    refetch: refetchOrganization,
  } = useQuery<any>({
    queryKey: [...queryKeys.organizations.detail(orgId || 0), 'myorg', selectedYear],
    queryFn: async () => {
      const raw = await organizationsApi.get(orgId!);
      return (raw as any)?.organization ?? raw;
    },
    enabled: !!orgId,
    staleTime: STALE_TIMES.ORGANIZATIONS,
  });

  const canEditBanner =
    user?.profile?.user_type === 'org_employee' &&
    user?.profile?.organization === organization?.id &&
    organization?.data_entry_open === true;

  const handleEditBanner = useCallback(() => {
    navigation.navigate('EditOrganizationBanner' as never);
  }, [navigation]);

  // Top-level organizational units query — same global caching.
  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    refetch: refetchUnits,
  } = useQuery({
    queryKey: [...queryKeys.organizationalUnits.list({
      department_id: orgId,
      parent__isnull: true,
    }), selectedYear],
    queryFn: () =>
      organizationalUnitsApi.list({
        department_id: orgId,
        parent__isnull: true,
        page_size: 100,
      }),
    enabled: !!orgId,
    staleTime: STALE_TIMES.ORGANIZATIONS,
  });

  // Total unit-goals across the org (including goals on child units, which
  // the per-top-level departments[].goals_count sum misses). page_size=1 so
  // we only need the paginator's `count` field.
  const { data: unitGoalsCountResponse } = useQuery({
    queryKey: ['unit-goals-count', orgId, selectedYear],
    queryFn: () => unitGoalsApi.list({ page_size: 1 }),
    enabled: !!orgId,
    staleTime: STALE_TIMES.ORGANIZATIONS,
  });

  const departments: DepartmentUnit[] = useMemo(
    () => unitsResponse?.results || [],
    [unitsResponse],
  );
  const totalEmployeesCount = organization?.actual_employee_count || 0;
  // Loading covers three cases:
  //   1. User is still loading from AuthContext (user === undefined/null)
  //   2. User loaded, orgId resolved, queries still fetching
  // Only fall through to the "no organization" error when user is known-loaded
  // AND they have no organization assigned.
  const userReady = !!user;
  const loading =
    !userReady || (!!orgId && (organizationLoading || unitsLoading));

  // Surface the "no organization assigned" state only after user has loaded.
  useEffect(() => {
    if (!userReady) {
      setError(null);
      return;
    }
    if (!orgId) {
      setError(t('myOrganization.noOrganizationAssigned'));
    } else {
      setError(null);
    }
  }, [userReady, orgId, t]);

  // Keep the org-structure file state in sync with the query result. This
  // preserves the existing upload/delete handlers that still mutate local
  // state directly.
  useEffect(() => {
    if (organization) {
      setOrgStructureFile(getFullMediaUrl(organization.org_structure_file) || null);
    }
  }, [organization]);

  // Back-compat shim for mutation handlers that still call fetchData().
  // Invalidates both organization + units so the next access refetches.
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.organizationalUnits.all });
  }, [queryClient]);

  // Cross-screen invalidation: mutation screens can still call
  // invalidate('my-organization') for explicit refreshes. React Query cache
  // is also invalidated automatically by api.ts's response interceptor on
  // any POST/PATCH/DELETE — this is a belt-and-braces hook.
  useEffect(() => {
    return subscribeInvalidation('my-organization', () => {
      fetchData();
    });
  }, [fetchData]);

  // Scroll to units section when navigated with scrollToUnits param
  useEffect(() => {
    if (route.params?.scrollToUnits && unitsSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: unitsSectionY.current, animated: true });
      }, 300);
    }
  }, [route.params?.scrollToUnits, loading]);

  // Scroll down to the org chart (departments + sections view)
  const scrollToChart = useCallback(() => {
    if (unitsSectionY.current > 0) {
      scrollViewRef.current?.scrollTo({ y: unitsSectionY.current, animated: true });
    }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refetchOrganization(), refetchUnits()]);
    } finally {
      setRefreshing(false);
    }
  }, [refetchOrganization, refetchUnits]);

  // Calculate totals for stats from real data
  const totalDepartments = departments.length;
  const totalSections = departments.reduce((sum, d) => sum + (d.children_count || 0), 0);
  const totalEmployees = departments.reduce((sum, d) => sum + (d.employees_count || 0), 0);
  // Use the paginated total — covers child-unit goals too. Falls back to
  // the per-top-level sum while the count query is loading.
  const totalUnitGoals = unitGoalsCountResponse?.count ??
    departments.reduce((sum, d) => sum + (d.goals_count || 0), 0);

  // Full-page skeleton only on cold load (no cached organization yet).
  // Warm cache revisits and background refetches fall through to the
  // regular render so the page feels instant. Matches GoalsScreen /
  // UnitGoalsScreen / etc.
  if (loading && !organization) {
    return (
      <PageContainer>
        <Navbar />
        <DashboardSkeleton />
      </PageContainer>
    );
  }

  if (error || !organization) {
    return (
      <PageContainer>
        <Navbar />
        <EmptyState
          icon="business-outline"
          title={t('navigation.myOrganization')}
          message={error || t('myOrganization.notFound')}
          actionLabel={t('common.refresh')}
          onAction={fetchData}
        />
      </PageContainer>
    );
  }


  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.myOrganization') },
          ]}
        />

        {organization ? (
          <OrganizationBanner
            organization={organization}
            canEdit={Boolean(canEditBanner)}
            onEditPress={handleEditBanner}
          />
        ) : null}

        {/* Stats Row - Order: الإدارات, الأقسام, الموظفين, أهداف الوحدات (RTL) */}
        <View style={[
          styles.statsRow,
          { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 20 }
        ]}>
          <TouchableOpacity
            style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => navigation.navigate('UnitGoalsStack' as any, { screen: 'UnitGoals' })}
            activeOpacity={0.7}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.unitGoals')}</Text>
              <View style={styles.statIconContainer}>
                <GoalsIcon />
              </View>
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalUnitGoals}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => navigation.getParent()?.navigate('EmployeesStack')}
            activeOpacity={0.7}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.employees')}</Text>
              <View style={styles.statIconContainer}>
                <EmployeesIcon />
              </View>
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalEmployeesCount}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={scrollToChart}
            activeOpacity={0.7}
            disabled={departments.length === 0}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.sections')}</Text>
              <View style={styles.statIconContainer}>
                <SectionsIcon />
              </View>
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalSections}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={scrollToChart}
            activeOpacity={0.7}
            disabled={departments.length === 0}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.departments')}</Text>
              <View style={styles.statIconContainer}>
                <DepartmentIcon />
              </View>
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalDepartments}</Text>
          </TouchableOpacity>
        </View>

        {/* Organizational Structure - Upload or Display */}
        <View style={[styles.orgStructureCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={styles.orgStructureHeader}>
            <Text style={[styles.orgStructureTitle, { color: themeColors.textPrimary }]}>{t('orgChart.orgStructure')}</Text>
            {orgStructureFile && user?.profile?.user_type !== 'org_executive' && (
              <View style={styles.orgStructureActions}>
                <View style={[styles.orgStructureActionBtn, { backgroundColor: tokens.colors.semantic.general["Main Color"], position: 'relative' }]}>
                  <Text style={styles.orgStructureActionBtnText}>
                    {isUploading ? t('orgChart.uploadingFile') : t('orgChart.changeFile')}
                  </Text>
                  {Platform.OS === 'web' && (
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ position: 'absolute', top: 0, start: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 } as any}
                      onChange={(e: any) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelected(file);
                        e.target.value = '';
                      }}
                    />
                  )}
                </View>
                <TouchableOpacity
                  style={[styles.orgStructureActionBtn, { backgroundColor: tokens.colors.raw.red["500"] }]}
                  onPress={handleDeleteOrgStructureFile}
                >
                  <Text style={styles.orgStructureActionBtnText}>{t('orgChart.deleteFile')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
          {orgStructureFile ? (
            <>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["1"], marginBottom: tokens.spacing.absolute["2"] }}>
                <TouchableOpacity
                  style={[styles.orgStructureZoomBtn, orgStructureZoom >= 1.5 && { opacity: 0.4 }]}
                  onPress={() => setOrgStructureZoom(prev => Math.min(prev + 0.1, 1.5))}
                  disabled={orgStructureZoom >= 1.5}
                >
                  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M21 21L16.65 16.65" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M11 8V14" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M8 11H14" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                </TouchableOpacity>
                <Text style={[styles.orgStructureZoomText, { color: themeColors.textMuted }]}>%{Math.round(orgStructureZoom * 100)}</Text>
                <TouchableOpacity
                  style={[styles.orgStructureZoomBtn, orgStructureZoom <= 0.3 && { opacity: 0.4 }]}
                  onPress={() => setOrgStructureZoom(prev => Math.max(prev - 0.1, 0.3))}
                  disabled={orgStructureZoom <= 0.3}
                >
                  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M21 21L16.65 16.65" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M8 11H14" stroke={themeColors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                </TouchableOpacity>
              </View>
              <View style={[styles.orgStructureImageWrapper, { backgroundColor: themeColors.background }]}>
                <View style={Platform.select({
                  web: { transform: `scale(${orgStructureZoom})`, transformOrigin: 'center top' } as any,
                  default: { transform: [{ scale: orgStructureZoom }] },
                })}>
                  <Image
                    source={{ uri: orgStructureFile }}
                    style={[styles.orgStructureImage, { aspectRatio: orgStructureAspectRatio }]}
                    resizeMode="contain"
                  />
                </View>
              </View>
            </>
          ) : (
            <View style={[styles.orgStructureEmpty, isUploading && { opacity: 0.6 }, { position: 'relative' }]}>
              {isUploading ? (
                <ActivityIndicator size="large" color={tokens.colors.semantic.general["Main Color"]} />
              ) : (
                <Svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <Path d="M40 30V18C40 16.9 39.1 16 38 16H26V12H30V4H18V12H22V16H10C8.9 16 8 16.9 8 18V30H4V38H12V30H8V18H22V30H18V38H26V30H22V18H36V30H32V38H40V30H36" stroke={themeColors.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              )}
              <Text style={[styles.orgStructureEmptyText, { color: themeColors.textMuted }]}>{t('orgChart.uploadHierarchy')}</Text>
              <Text style={[styles.orgStructureHintText, { color: themeColors.textMuted }]}>{t('orgChart.allowedTypes')}</Text>
              {Platform.OS === 'web' && user?.profile?.user_type !== 'org_executive' && !isUploading && (
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ position: 'absolute', top: 0, start: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 } as any}
                  onChange={(e: any) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelected(file);
                    e.target.value = '';
                  }}
                />
              )}
            </View>
          )}
        </View>

        {/* Organizational Chart Section */}
        <View style={styles.chartSection} onLayout={(e) => { unitsSectionY.current = e.nativeEvent.layout.y; }}>
          <OrganizationalChart
            organizationId={user?.profile?.organization}
            organizationName={translateOrg(organization.name)}
            onNodePress={(node) => {
              navigation.navigate('OrganizationalUnitDetail', { id: node.id });
            }}
            onMoveComplete={fetchData}
          />
        </View>

        {/* Section Title Card with Add Button */}
        <View style={[styles.sectionTitleCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["3"], alignItems: 'stretch' }, user?.profile?.user_type === 'org_executive' && { justifyContent: 'flex-end' }]}>
          {canAdd && (
            <Button
              variant="primary"
              onPress={() => navigation.navigate('AddOrganizationalUnit', {})}
              trailingIcon={<SquarePlusIcon />}
            >
              {t('organizationalUnits.addUnit')}
            </Button>
          )}
          <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('sidebar.organizationalUnits')}</Text>
        </View>

        {/* Departments Grid */}
        {departments.length > 0 ? (
          <View style={[styles.departmentsGrid, { gap: isMobile ? 12 : 16 }]}>
            {departments.map((dept: DepartmentUnit, index: number) => (
              <View key={dept.id || index} style={[styles.departmentCard, { width: departmentCardWidth, minWidth: isMobile ? undefined : 300, backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                {/* Main Content Row */}
                <View style={styles.cardContent}>
                  {/* Left side - Progress Circle */}
                  <View style={styles.progressContainer}>
                    <CircularProgress
                      percentage={dept.achievement_percentage || 0}
                    />
                  </View>

                  {/* Right side - Info */}
                  <View style={styles.cardInfo}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"] }}>
                      <Text style={[styles.departmentName, { color: themeColors.textPrimary }]}>{translateUnit(dept.name_ar || dept.name)}</Text>
                      {(dept as any).has_pending_deletion && <PendingDeletionBadge compact />}
                    </View>
                    <Text style={[styles.departmentDescription, { color: themeColors.textMuted }]} numberOfLines={2}>
                      {translate(dept.description) || '-'}
                    </Text>

                    {/* Stats List - Vertical layout matching Figma */}
                    <View style={styles.statsList}>
                      <StatRow label={t('myOrganization.sections')} value={dept.children_count ?? 0} />
                      <StatRow label={t('common.kpis')} value={dept.kpis_count ?? 0} />
                      <StatRow label={t('navigation.processes')} value={dept.processes_count ?? 0} />
                      <StatRow label={t('myOrganization.employees')} value={dept.employees_count ?? 0} />
                    </View>
                  </View>
                </View>

                {/* Footer - View Details + Edit/Delete actions */}
                <View style={styles.cardFooter}>
                  <Button
                    variant="secondary"
                    onPress={() => navigation.navigate('OrganizationalUnitDetail', { id: dept.id })}
                    leadingIcon={<ArrowLeftIcon />}
                    style={{ flex: 1 }}
                  >
                    {t('common.viewDetails')}
                  </Button>
                  {(canEdit || canDelete) && (
                    <View style={styles.cardActions}>
                      {canEdit && (
                        <IconButton
                          variant="secondary"
                          onPress={() => handleEditDept(dept)}
                          icon={<EditIcon />}
                          accessibilityLabel={t('common.edit')}
                        />
                      )}
                      {canDelete && (
                        <IconButton
                          variant="destructive"
                          onPress={() => handleDeleteDept(dept)}
                          icon={<TrashIcon />}
                          accessibilityLabel={t('common.delete')}
                        />
                      )}
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('myOrganization.noDepartments')}</Text>
            <Text style={[styles.emptySubText, { color: themeColors.textMuted }]}>{t('myOrganization.addDepartmentHint')}</Text>
          </View>
        )}

      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setDeptToDelete(null); }}
        onConfirm={confirmDeleteDept}
        entityName={deptToDelete?.name_ar || deptToDelete?.name || ''}
        fetchImpact={async () => {
          if (!deptToDelete) return { total_affected: 0, can_restore: true, items: [], unlinked_kpis: [] };
          const response = await organizationalUnitsApi.deletionImpact(deptToDelete.id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: tokens.spacing.absolute["10"],
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#D4D4D4',
    fontFamily: FONTS.regular,
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general["Main Color"],
    fontFamily: FONTS.medium,
  },
  // Stats Row
  statsRow: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  statCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  statIconContainer: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statCardLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
    fontFamily: FONTS.regular,
  },
  statCardValue: {
    fontSize: 30,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    lineHeight: 40,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  // Organizational Structure Card
  orgStructureCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  orgStructureHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  orgStructureTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    textAlign: getTextAlign(isRTL),
  },
  orgStructureActions: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["2"],
  },
  orgStructureActionBtn: {
    paddingVertical: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-6"],
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  orgStructureActionBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  orgStructureEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderStyle: 'dashed',
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["3"],
  },
  orgStructureEmptyText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
  orgStructureHintText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
    opacity: 0.7,
  },
  orgStructureZoomControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  orgStructureZoomBtn: {
    padding: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-6"],
  },
  orgStructureZoomText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    minWidth: 36,
    textAlign: 'center',
  },
  orgStructureImageWrapper: {
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'hidden',
  },
  orgStructureImage: {
    width: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  // Organizational Chart Section
  chartSection: {
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Section Title Card with Add Button
  sectionTitleCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    minHeight: 44,
    gap: tokens.spacing.absolute["1_5"],
  },
  addButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["50"],
    fontFamily: FONTS.medium,
  },
  sectionTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  // Departments Grid
  departmentsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  departmentCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["2"],
  },
  progressContainer: {
    // Progress circle container
  },
  progressText: {
    position: 'absolute',
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  cardInfo: {
    flex: 1,
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
  },
  departmentName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  departmentDescription: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
    fontFamily: FONTS.regular,
  },
  // Stats List - Vertical layout matching Figma
  statsList: {
    width: '100%',
    gap: tokens.spacing.absolute["0"],
  },
  // Stat Row - Label pill on right, value on left
  statRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    width: '100%',
  },
  statRowValue: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    flex: 1,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  statLabelPill: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    width: 80,
    alignItems: 'flex-end',
  },
  statLabelText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  // Card footer row
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.slate["100"],
    paddingTop: tokens.spacing.absolute["1"],
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  // Delete Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    width: 340,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["4"],
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    textAlign: getTextAlign(isRTL),
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["300"],
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["700"],
    fontFamily: FONTS.medium,
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.red["500"],
    alignItems: 'center',
  },
  modalButtonDisabled: {
    opacity: 0.6,
  },
  modalDeleteButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  // Empty State
  emptyContainer: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["10"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["2"],
    fontFamily: FONTS.medium,
  },
  emptySubText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
});

const styles = getStyles(true);
